/**
 * 对局会话 —— **只有一个状态源**
 *
 * 设计（GDD 8.3 / 实施计划决策 4）：客户端不维护自己的状态机，
 * 而是持有 `actions: Action[]`，每次操作后调用同一份 `simulate()` 全量重算。
 *
 * 好处：
 * - 客户端与服务端跑的是同一份函数、同一份输入，异步对战的复算校验天然成立；
 * - 不存在"本地状态与权威状态漂移"这类最难查的 bug；
 * - 录制 `actions` 就等于录像，回放、云存档、断线续玩共用一套机制。
 *
 * 成本：一次重算 O(操作数 × 区域数)，实测在 1ms 量级（40 回合 × 24 区域），
 * 远远小于一帧的预算，因此不做增量优化 —— 简单优先。
 */

import {
  buildMatchSetup,
  computeStars,
  describeOptions,
  resolveConstants,
  simulate,
  type Action,
  type LevelConfig,
  type MatchSetup,
  type OptionView,
  type SimEvent,
  type SimResult,
  type SimState,
} from "@chuandeng/shared";

export interface GameSessionInit {
  level: LevelConfig;
  mapSeed: number;
  eventSeed: number;
  actions?: Action[];
}

export class GameSession {
  readonly setup: MatchSetup;
  readonly level: LevelConfig;
  private actionList: Action[];
  private cached: SimResult | null = null;
  private cachedOptions: OptionView | null = null;
  private consumedEvents = 0;

  constructor(init: GameSessionInit) {
    this.level = init.level;
    this.setup = buildMatchSetup({
      level: init.level,
      mapSeed: init.mapSeed,
      eventSeed: init.eventSeed,
    });
    this.actionList = init.actions ? [...init.actions] : [];
  }

  get actions(): readonly Action[] {
    return this.actionList;
  }

  get result(): SimResult {
    if (!this.cached) this.cached = simulate(this.setup, this.actionList);
    return this.cached;
  }

  get state(): SimState {
    return this.result.finalState;
  }

  get options(): OptionView {
    if (!this.cachedOptions) this.cachedOptions = describeOptions(this.setup, this.state);
    return this.cachedOptions;
  }

  get finished(): boolean {
    return this.state.finished;
  }

  get outcome(): "win" | "lose" | null {
    return this.state.outcome;
  }

  get stars(): number {
    return computeStars(this.state, resolveConstants(this.setup.constants));
  }

  get checksum(): string {
    return this.result.checksum;
  }

  /** 本局目标与进度 */
  get goal(): { current: number; target: number; ratio: number } {
    return {
      current: this.options.averageEnlightenment,
      target: this.setup.goalEnlightenment,
      ratio: this.options.goalProgress,
    };
  }

  // ---------- 操作 ----------

  spread(routeId: string): boolean {
    return this.push({ t: "spread", routeId });
  }

  ignite(regionId: string): boolean {
    return this.push({ t: "ignite", regionId });
  }

  upgrade(nodeId: string): boolean {
    return this.push({ t: "upgrade", nodeId });
  }

  answer(questionId: string, correct: boolean, cardId?: string): boolean {
    return this.push(cardId ? { t: "answer", questionId, correct, cardId } : { t: "answer", questionId, correct });
  }

  endTurn(): boolean {
    return this.push({ t: "endTurn" });
  }

  /**
   * 推进一个动作。返回是否真的改变了状态 —— 无效动作不入队，
   * 这样 `actions` 始终是干净的回放记录（服务端复算不会因为垃圾动作失配）。
   */
  private push(action: Action): boolean {
    if (this.finished) return false;

    const candidate = [...this.actionList, action];
    const result = simulate(this.setup, candidate);
    const changed = result.checksum !== this.result.checksum;
    if (!changed) return false;

    this.actionList = candidate;
    this.cached = result;
    this.cachedOptions = null;
    return true;
  }

  /** 取出尚未消费的新事件（用于叙事演出与弹题） */
  drainEvents(): SimEvent[] {
    const all = this.result.events;
    if (this.consumedEvents >= all.length) return [];
    const fresh = all.slice(this.consumedEvents);
    this.consumedEvents = all.length;
    return fresh;
  }

  /** 最近的若干条事件（侧栏日志用，不消费游标） */
  recentEvents(limit = 3): SimEvent[] {
    return this.result.events.slice(-limit);
  }

  /** 供云存档/结算上报使用 */
  snapshot(): { levelId: string; mapSeed: number; eventSeed: number; actions: Action[] } {
    return {
      levelId: this.level.id,
      mapSeed: this.setup.mapSeed,
      eventSeed: this.setup.eventSeed,
      actions: [...this.actionList],
    };
  }
}

/** 构造会话（入口保持函数形式，方便以后替换成从云存档恢复的实现） */
export function createSession(init: GameSessionInit): GameSession {
  return new GameSession(init);
}
